import React, { useEffect, useRef, useState } from "react";
import { Turnstile } from "@marsidev/react-turnstile";

/**
 * Runtime-config Turnstile wrapper.
 *
 * The site key is served by the backend at `GET /api/config` rather than
 * baked into the frontend bundle at build time. This decouples us from the
 * build pipeline: rotating the key only requires updating the backend env
 * (via Emergent Secret Management) and restarting the backend.
 *
 * A single module-level fetch is deduplicated across every widget on the
 * page and cached for the lifetime of the tab. `useTurnstileSiteKey()` is
 * exported for the form pages that gate the submit button on key
 * availability.
 */
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Cache the site key across every widget & hook consumer on the page.
// `null`   → not yet resolved
// `""`     → resolved, but no key configured (widget renders nothing)
// string   → resolved with a real key
let _cached = null;
let _inflight = null;
const _subscribers = new Set();

function _notify(key) {
  _cached = key;
  _subscribers.forEach((cb) => {
    try {
      cb(key);
    } catch {
      /* subscriber errors must not break the chain */
    }
  });
  _subscribers.clear();
}

function fetchTurnstileSiteKey() {
  if (_cached !== null) return Promise.resolve(_cached);
  if (_inflight) return _inflight;
  _inflight = fetch(`${API}/config`, { credentials: "omit" })
    .then((r) => (r.ok ? r.json() : { turnstile_site_key: "" }))
    .then((body) => {
      const key = typeof body?.turnstile_site_key === "string" ? body.turnstile_site_key : "";
      _notify(key);
      return key;
    })
    .catch(() => {
      _notify("");
      return "";
    })
    .finally(() => {
      _inflight = null;
    });
  return _inflight;
}

/**
 * React hook — returns the resolved site key, or `null` while pending.
 * Use to gate the submit button while the config is loading:
 *   const siteKey = useTurnstileSiteKey();
 *   const captchaRequired = siteKey !== "" && siteKey !== null;
 *   const captchaReady = siteKey === "" || !!token;
 */
export function useTurnstileSiteKey() {
  const [key, setKey] = useState(_cached);
  useEffect(() => {
    if (_cached !== null) return undefined;
    let mounted = true;
    const cb = (k) => {
      if (mounted) setKey(k);
    };
    _subscribers.add(cb);
    fetchTurnstileSiteKey();
    return () => {
      mounted = false;
      _subscribers.delete(cb);
    };
  }, []);
  return key;
}

// Test-only: reset the cache between unit tests. Not intended for prod code paths.
export function __resetTurnstileCacheForTests() {
  _cached = null;
  _inflight = null;
  _subscribers.clear();
}

/**
 * Convenience hook for the form pages — returns true while the submit button
 * should be disabled *because of the captcha*. Callers still combine this
 * with their own `submitting` / validation state.
 *
 *   const captchaBlocks = useSubmitBlockedByTurnstile(captchaToken);
 *   <button disabled={submitting || captchaBlocks} />
 */
export function useSubmitBlockedByTurnstile(token) {
  const key = useTurnstileSiteKey();
  if (key === null) return true; // config still loading
  if (key === "") return false; // no captcha configured — allow
  return !token; // captcha configured — need a fresh token
}

const TurnstileWidget = ({ onToken, action = "form_submit", testid = "turnstile-widget" }) => {
  const siteKey = useTurnstileSiteKey();
  const ref = useRef(null);

  // When the config resolves with no key (`""`), the widget silently disables
  // itself and emits an empty token so the parent form can still submit —
  // matches the backend's "skip verification when secret unset" behaviour.
  useEffect(() => {
    if (siteKey === "") onToken?.("");
    // Deliberately mount-only; re-emitting on every prop change would cause a
    // token loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteKey === ""]);

  if (siteKey === null) {
    // Config still loading — render a tiny placeholder so the form layout
    // doesn't jump when the widget appears.
    return <div className="my-4 h-[65px]" data-testid={`${testid}-loading`} aria-hidden="true" />;
  }
  if (siteKey === "") return null;

  return (
    <div className="my-4" data-testid={testid}>
      <Turnstile
        ref={ref}
        siteKey={siteKey}
        options={{ action, theme: "light", size: "flexible" }}
        onSuccess={(token) => onToken?.(token)}
        onExpire={() => onToken?.("")}
        onError={() => onToken?.("")}
      />
    </div>
  );
};

export default TurnstileWidget;
